/**
 * ViewModels only → greenfield FlightDetailsScreen offer contract.
 * Never accepts rawDeal / provider payloads.
 */

import { mapDetailsBaggageDisplay } from "./mapDetailsBaggageDisplay";
import { mapDetailsBookingSummary } from "./mapDetailsBookingSummary";
import { formatFlightReviewDisplayMoney } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/resolveFlightReviewDisplayMoney";

/**
 * @param {{
 *   offerVm: import("../view-models/types").FlightOfferViewModel|null,
 *   detailsVm: object|null,
 *   priceVm: import("../view-models/types").BookingPriceViewModel|null,
 *   searchVm?: object|null,
 * }} input
 */
export function toGreenfieldDetailsOffer(input = {}) {
  const { offerVm, detailsVm, priceVm } = input;
  if (!offerVm?.id || !detailsVm?.id) return null;

  // Guard: presentation mapper must never receive raw
  assertNoRaw(input);

  const baggageDisplay = mapDetailsBaggageDisplay(detailsVm.baggage || offerVm.baggage, {
    cabin: detailsVm.fare?.cabin || offerVm.fare?.cabin,
    fareType: detailsVm.fare?.fareType || offerVm.fare?.fareType,
  });

  const bookingSummary = mapDetailsBookingSummary(priceVm || offerVm.price, {
    fare: detailsVm.fare || offerVm.fare,
    baggageDisplay,
  });

  const conditions = mapConditions(detailsVm.conditions);
  const display = (priceVm || offerVm.price)?.display || null;
  const pax = Math.max(
    1,
    Number((priceVm || offerVm.price)?.passengerPriceBreakdown?.totalPaxCount) || 1
  );

  return {
    id: String(offerVm.id),
    airline: detailsVm.airline,
    secondaryAirline: null,
    fareBadges: detailsVm.fareBadges || [],
    outbound: detailsVm.outbound,
    inbound: detailsVm.inbound,
    baggage: baggageDisplay,
    fare: {
      cabin: detailsVm.fare?.cabin || null,
      family: detailsVm.fare?.fareFamily || detailsVm.fare?.fareType || null,
    },
    price: {
      perPassengerLabel: formatPerPassengerLabel(display),
      perPassengerValue: display?.sortPrice ?? null,
      quantity: pax,
      totalLabel:
        display?.displayedTotalIls != null
          ? formatFlightReviewDisplayMoney(display.displayedTotalIls, "ILS")
          : null,
      totalNote: pax > 1 ? `סה״כ ל-${pax} נוסעים` : "סה״כ",
      lineLabel: null,
    },
    conditions,
    alerts: Array.isArray(detailsVm.alerts) ? detailsVm.alerts : [],
    bookingSummary,
    commission: bookingSummary?.commission || null,
  };
}

/**
 * @param {object} input
 */
export function assertNoRaw(input) {
  const forbidden = ["rawDeal", "raw", "providerPayload", "rawOffer"];
  for (const key of forbidden) {
    if (
      input &&
      Object.prototype.hasOwnProperty.call(input, key) &&
      input[key] != null
    ) {
      throw new Error(
        `toGreenfieldDetailsOffer must not receive ${key} — ViewModels only`
      );
    }
  }
  // Deep-ish: offerVm / detailsVm must not expose rawDeal
  for (const vm of [input.offerVm, input.detailsVm, input.priceVm]) {
    if (vm && Object.prototype.hasOwnProperty.call(vm, "rawDeal") && vm.rawDeal != null) {
      throw new Error("ViewModel must not carry rawDeal into presentation mapper");
    }
  }
}

function mapConditions(conditions) {
  if (!conditions || typeof conditions !== "object") {
    return {
      changes: null,
      cancellation: null,
      refund: null,
      ticketingDeadline: null,
      fullTerms: null,
    };
  }
  return {
    changes: str(conditions.changePolicy),
    cancellation: str(conditions.cancelPolicy),
    refund: str(conditions.refundability),
    ticketingDeadline: str(conditions.ticketingDeadline),
    fullTerms: str(conditions.fullTerms),
  };
}

function formatPerPassengerLabel(display) {
  if (!display) return null;
  if (display.isPrimaryCurrencyIls && display.displayedIls != null) {
    return formatFlightReviewDisplayMoney(display.displayedIls, "ILS");
  }
  if (display.displayedUsd != null) {
    return formatFlightReviewDisplayMoney(display.displayedUsd, "USD");
  }
  if (display.displayedIls != null) {
    return formatFlightReviewDisplayMoney(display.displayedIls, "ILS");
  }
  if (display.sortPrice != null) {
    return formatFlightReviewDisplayMoney(
      display.sortPrice,
      display.isPrimaryCurrencyIls ? "ILS" : "USD"
    );
  }
  return null;
}

function str(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s || null;
}

/**
 * Detect if a presentation offer leaked raw / provider keys (tests).
 * @param {object} offer
 */
export function greenfieldDetailsOfferHasRaw(offer) {
  if (!offer || typeof offer !== "object") return false;
  if (Object.prototype.hasOwnProperty.call(offer, "rawDeal") && offer.rawDeal != null) {
    return true;
  }
  if (Object.prototype.hasOwnProperty.call(offer, "raw") && offer.raw != null) {
    return true;
  }
  return false;
}
